import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { MapGeoSelectModalProps } from './MapGeoSelectModal';
import { applyGeopolygonThumbnail } from '../utils/generateGeopolygonMapThumbnail';
import {
  appendClearPolygonButton,
  clearGeoPolygonFromDraw,
  createMapboxDrawStyles,
  loadGeoPolygonIntoDraw,
} from '../utils/mapboxDrawStyles';

const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGF2aWQtcGV0dGlmb3ItbmQiLCJhIjoiY200dDNjbWtpMDdlZjJqcHM1ODhkOWF4OSJ9.PQL7pNkiko1SbYsmNrvacQ';

export default function MapGeoSelect({
  setShowGeoMap,
  center,
  zoom,
  width,
  height,
  geoPolygon,
  setGeoPolygon,
  setGeoPolygonImage,
}: MapGeoSelectModalProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const initializeMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [center[1], center[0]],
      zoom: zoom,
      attributionControl: true,
    });

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        // Built-in trash only removes vertices in direct_select; we add a custom control.
        trash: false,
      },
      defaultMode: 'draw_polygon',
      styles: createMapboxDrawStyles(),
    });
    drawRef.current = draw;

    function clearPolygonState() {
      setGeoPolygon('');
      setGeoPolygonImage('');
    }

    function syncPolygonFromDraw() {
      const data = draw.getAll();
      if (
        data.features.length > 0 &&
        data.features[0].geometry.type === 'Polygon'
      ) {
        const ring = data.features[0].geometry.coordinates[0] as number[][];
        setGeoPolygon(JSON.stringify(ring));
        void applyGeopolygonThumbnail(ring)
          .then(setGeoPolygonImage)
          .catch((err) => {
            console.warn('Could not generate geopolygon map preview:', err);
          });
      } else {
        clearPolygonState();
      }
    }

    initializeMap.on('load', () => {
      map.current = initializeMap;
      loadGeoPolygonIntoDraw(draw, initializeMap, geoPolygon);
      if (mapContainer.current) {
        appendClearPolygonButton(mapContainer.current, draw, clearPolygonState);
      }
    });

    // add map scale
    initializeMap.addControl(new mapboxgl.ScaleControl(), 'bottom-right');
    // add map zoom control without rotation icon
    initializeMap.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    // draw control (polygon tool; custom trash appended to same button group on load)
    initializeMap.addControl(draw);

    initializeMap.on('draw.create', syncPolygonFromDraw);
    initializeMap.on('draw.update', syncPolygonFromDraw);
    initializeMap.on('draw.delete', () => {
      clearGeoPolygonFromDraw(draw);
      clearPolygonState();
    });

    // allow single polygon only
    initializeMap.on("draw.modechange", () => {
      const data = draw.getAll();
      if (draw.getMode() === "draw_polygon") {
        const pids: string[] = [];
        const lid = data.features[data.features.length - 1]?.id;
        data.features.forEach((f) => {
          if (f.geometry.type === "Polygon" && f.id !== lid && f.id) {
            pids.push(String(f.id));
          }
        });
        if (pids.length > 0) {
          draw.delete(pids);
        }
      }
    });

    // create custom submit button on the map
    class SubmitButton {
      onAdd() {
        const div = document.createElement("div");
        div.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
        div.innerHTML = `
          <button type="button" aria-label="Submit geopolygon">
            <svg fill="#000000" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/>
              <title>Submit</title>
            </svg>
          </button>
        `;

        div.addEventListener("contextmenu", (e) => e.preventDefault());
        div.addEventListener("click", () => {
          setShowGeoMap(false);
        });

        return div;
      }
      onRemove(){}
    }

    initializeMap.addControl(new SubmitButton());

    return () => {
      drawRef.current = null;
      initializeMap.remove();
    };
  }, []);

  return (
    <div style={{ width: width, height: height, position: 'relative' }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
